import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Check, MapPin, FileText } from "lucide-react";
import { getOnboardingPortal, completeOnboardingItem, onboardingItemFileUrl } from "@/lib/api";

const LABEL = { information: "Review Required", acknowledgment: "Acknowledgment Required", signature: "Signature Required" };

export default function OnboardingPortal() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [nf, setNf] = useState(false);
  const [active, setActive] = useState(null);
  const [legalName, setLegalName] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(() => getOnboardingPortal(token).then(setD).catch(() => setNf(true)), [token]);
  useEffect(() => { load(); }, [load]);

  if (nf) return <div className="mh"><div className="mh-empty">This onboarding link is not valid.</div></div>;
  if (!d) return <div className="mh"><div className="mh-empty">Loading…</div></div>;

  const open = (it) => { setActive(it); setLegalName(""); setConsent(false); setErr(""); };
  const submit = async () => {
    if (busy) return; setBusy(true); setErr("");
    try {
      await completeOnboardingItem(token, active.itemId, { legalName, consent });
      setActive(null); await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Please complete the required affirmation."); }
    finally { setBusy(false); }
  };

  const done = d.items.filter((i) => i.status === "complete").length;

  return (
    <div className="mh" data-testid="onboarding-portal"><div className="mh-wrap ca-wrap">
      <header className="mh-head">
        <div className="mh-agency">MAKE IT HAPPEN PHILLY</div>
        <h1 className="mh-headtitle">Complete Your Onboarding</h1>
      </header>
      <div className="ca-confirm-card">
        <div className="ca-emp">{d.name}</div>
        <div className="ca-loc"><MapPin size={15} /> {d.employer}</div>
        <div className="ca-time">Position · {d.position || "—"}</div>
        <div className="ca-time">Shift · {d.shift || "—"} · Start {d.startDate || "—"}</div>
      </div>

      <div className="mh-progress" style={{ margin: "16px 0 4px" }}>
        <strong data-testid="onboarding-progress">{done} of {d.items.length} complete</strong>
        {d.mihpComplete && <span data-testid="onboarding-mihp-complete" style={{ marginLeft: 8, color: "var(--mihp-green)", fontWeight: 700 }}>MIHP Onboarding Requirements Complete ✓</span>}
      </div>

      {d.items.map((it) => (
        <div key={it.itemId} className="permalink-url" data-testid={`onb-item-${it.itemId}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div>
            <div style={{ fontWeight: 600 }}>{it.title} {it.requirement === "Optional" && <span className="mut">(optional)</span>}</div>
            <div className="mut" style={{ fontSize: ".75rem" }}>v{it.version} · {it.status === "complete" ? "Complete" : LABEL[it.action]}</div>
            {it.hasFile && (
              <a className="onb-viewdoc" href={onboardingItemFileUrl(token, it.itemId)} target="_blank" rel="noreferrer" data-testid={`onb-viewdoc-${it.itemId}`}>
                <FileText size={13} /> View document
              </a>
            )}
          </div>
          {it.status === "complete"
            ? <span style={{ color: "var(--mihp-green)", fontWeight: 700 }} data-testid={`onb-complete-${it.itemId}`}><Check size={16} /></span>
            : <button className="btn sm" onClick={() => open(it)} data-testid={`onb-open-${it.itemId}`}>{it.action === "signature" ? "Sign" : it.action === "acknowledgment" ? "Acknowledge" : "Review"}</button>}
        </div>
      ))}

      {d.mihpComplete && (
        <div className="ca-bring" style={{ marginTop: 16 }} data-testid="continue-adp">
          <h2>Continue to ADP Employment Setup</h2>
          <p>Your Make It Happen Philly onboarding items are complete. The next step (tax, banking and I-9 setup) is handled securely through ADP and will be provided separately. You are not yet fully onboarded.</p>
        </div>
      )}

      {active && (
        <div className="modal-scrim" onClick={() => setActive(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="onb-modal">
            <div className="modal-head"><h2>{active.title}</h2></div>
            <div className="modal-body">
              <p className="mut" style={{ marginBottom: 12 }}>Document version {active.version}. Please review the {active.title} policy.</p>
              {active.hasFile && (
                <a className="btn" href={onboardingItemFileUrl(token, active.itemId)} target="_blank" rel="noreferrer" data-testid="onb-modal-viewdoc" style={{ marginBottom: 12, display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <FileText size={14} /> View document (v{active.version})
                </a>
              )}
              {active.action === "signature" && (
                <input className="ca-input" placeholder="Type your full legal name" value={legalName} onChange={(e) => setLegalName(e.target.value)} data-testid="onb-legalname" />
              )}
              <label className="ca-ack" data-testid="onb-consent">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="onb-consent-check" />
                <span>{active.action === "signature" ? "I agree to sign this document electronically and affirm the above is my legal name." : "I acknowledge that I have reviewed this document."}</span>
              </label>
              {err && <div className="mh-err" data-testid="onb-error">{err}</div>}
              <button className="mh-save" disabled={busy} onClick={submit} data-testid="onb-submit">{busy ? "Saving…" : "Confirm"}</button>
            </div>
          </div>
        </div>
      )}
    </div></div>
  );
}
